import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import MobileMenuDropdown from '../dialogs/MobileMenuDropdown';
import useCart from 'hooks/useCart';
import { useRestaurantContext } from 'context/RestaurantContext';

const TopBarHeader = ({
  title,
  showSearch = false,
  showBack = false,
  onSearchChange,
  rightNode,
  className = '',
  hideCart = false,
  hideMenu = false
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, isGuest, signOutUser, openAuthModal, openGuestSignOutDialog } = useAuth();
  const { details } = useRestaurantContext();
  const { cartData } = useCart();
  const cartCount = cartData?.items?.length ||0;
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
   const previousPathRef = useRef(location.pathname);

   useEffect(() => {
     // Update the ref with the new path
     previousPathRef.current = location.pathname;
     console.log('previous',previousPathRef.current)
     console.log('current',location.pathname)
   }, [location]);

  const handleSignOut = () => {
    if (isGuest) {
      openGuestSignOutDialog();
    } else {
      signOutUser();
    }
  };

  const handleBack = () => {
    if (previousPathRef.current !== location.pathname) {
      navigate(-1);
    } else {
      navigate(`..`, { relative: 'path' });
    }
  };
  const handleCartClick = () => navigate('checkout');
  const handleLogoClick = () => navigate(`..`, { relative: 'path' });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onSearchChange) onSearchChange(value);
  };

  const getContextualTitle = () => {
    if (title) return title;
    
    switch (location.pathname) {
      case `/restaurant/${details?.id || ''}`:
        return 'Restaurant';
      case `/restaurant/${details?.id || ''}/menu`:
        return 'Menu';
      case `/restaurant/${details?.id || ''}/checkout`:
        return 'Your Cart';
      case `/restaurant/${details?.id || ''}/checkout-flow`:
        return 'Checkout';
      case `/restaurant/${details?.id || ''}/orders`:
        return 'My Orders';
      default:
        return 'QR Menu Pro';
    }
  };

  const shouldShowSearch = showSearch ;
  const shouldShowBack = showBack || location.pathname === '/checkout-flow';
  const checkIsOnCartPage = location.pathname === '/shopping-cart';
  const isOrdersPage = location.pathname.includes('/orders');
  const shouldShowCart = !checkIsOnCartPage && !isOrdersPage && !hideCart;

  return (
    <header
      className={`sticky top-0 z-header bg-card/95 backdrop-blur-header border-b border-border safe-area-top ${className}`}
    >
      <div className="flex items-center justify-between h-14 px-4 lg:px-6 lg:ml-60">
        {/* Left Section */}
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          {shouldShowBack && (
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-smooth lg:hidden"
            >
              <Icon name="ArrowLeft" size={20} />
            </button>
          )}

          {/* Desktop Title */}
          <div className="hidden lg:block">
            <h1 className="text-xl font-semibold text-foreground">
              {getContextualTitle()}
            </h1>
          </div>
          {/* Mobile Title */}
          <div className="flex lg:hidden text-lg font-semibold" onClick={() => navigate(`..`, { relative: 'path' })}>
            {isOrdersPage ? getContextualTitle() : (details?.name ?? null)}
          </div>
          {/* Search Bar */}
          {shouldShowSearch && (
            <div className="hidden lg:flex  max-w-md mx-4 lg:mx-0 min-w-[200px]">
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-smooth ${
                    isSearchFocused ? "text-primary" : "text-muted-foreground"
                  }`}
                />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className={`w-full pl-10 pr-4 py-2 text-sm bg-muted border border-transparent rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    isSearchFocused ? "bg-background" : ""
                  }`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center space-x-2">
          {rightNode}
          {shouldShowCart && (
            <button
              onClick={handleCartClick}
              className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-smooth"
            >
              <Icon name="ShoppingCart" size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-accent text-accent-foreground text-xs font-medium rounded-full flex items-center justify-center px-1 animate-scale-in">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>
          )}

          {/* Auth Status (Desktop) */}
          <div className="relative hidden lg:block">
            {currentUser ? (
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="w-full text-left px-2 py-1.5 text-sm text-primary hover:bg-muted rounded-md"
              >
                {currentUser.photoURL ? (
                  <img
                    src={currentUser.photoURL}
                    alt="User"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon
                    name="User"
                    color="green"
                    size={20}
                    className="text-muted-foreground"
                  />
                )}
              </button>
            ) : (
              <button
                onClick={() => openAuthModal()}
                className="w-full text-left px-2 py-1.5 text-sm  hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="User" className="mr-2" size={16} />
              </button>
            )}

            {isProfileOpen && currentUser && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-card border rounded-md shadow-lg z-50">
                <div className="p-2 border-b">
                  <p className="text-sm font-medium truncate">
                    {currentUser.email}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {currentUser.isAnonymous ? "Guest User" : "Registered User"}
                  </p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleSignOut}
                    className=" flex  w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-md items-center"
                  >
                    <Icon
                      name="LogOut"
                      className="mr-2 text-sm text-red-500"
                      size={16}
                    />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          {!hideMenu && (
            <div className="relative lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-smooth"
            >
              <Icon name="MoreVertical" size={20} />
            </button>
            {isMobileMenuOpen && (
              <MobileMenuDropdown
                cartCount={cartCount}
                onClose={() => setIsMobileMenuOpen(false)}
              />
            )}
          </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBarHeader;